import { z } from 'zod'

import { TRAINER_CONTRACT_VERSION } from './trainer-event-read'

export const TRAINER_EVENT_FEEDBACK_READ_CONTRACT_VERSION = TRAINER_CONTRACT_VERSION

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const trainerEventFeedbackReadQuerySchema = z.object({
  orgId: optionalQueryString,
  type: optionalQueryString,
  status: optionalQueryString,
  source: optionalQueryString,
  limit: optionalQueryString,
}).passthrough()

const nullableString = z.string().nullable()
const scalarAnswer = z.union([z.string(), z.number().finite(), z.null()])
const customAnswers = z.record(scalarAnswer)

const trainerEventFeedbackReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  r2Touched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

const trainerFeedbackTypeSchema = z.enum(['trainer', 'content'])
const trainerFeedbackStatusSchema = z.enum(['submitted', 'reviewed', 'archived'])
const trainerFeedbackSourceSchema = z.enum(['trainer_app', 'participant_intake', 'migration', 'admin'])

export const trainerEventFeedbackSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  eventId: z.string().min(1),
  createdByUserId: z.string().min(1),
  submittedByUserId: z.string().min(1),
  feedbackType: trainerFeedbackTypeSchema,
  rating: z.number().finite().nullable(),
  comment: z.string(),
  customAnswers,
  status: z.string(),
  source: z.string(),
  active: z.boolean(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
  readOnly: z.boolean(),
  origin: nullableString,
}).passthrough()

const trainerEventFeedbackEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([
    z.literal('trainer_events'),
    z.literal('trainer_event_feedback'),
    z.literal('trainer_certificate_submissions'),
  ]),
  projection: z.literal('trainer-event-feedback'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerEventFeedbackListResponseSchema = z.object({
  contract_version: z.literal(TRAINER_EVENT_FEEDBACK_READ_CONTRACT_VERSION),
  feedback: z.array(trainerEventFeedbackSummarySchema),
  feedbackEvidence: trainerEventFeedbackEvidenceSchema,
  sideEffects: trainerEventFeedbackReadSideEffectsSchema,
}).passthrough()

const trainerPublicFormFieldOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
}).passthrough()

const trainerPublicFormFieldSchema = z.object({
  name: z.string().min(1),
  label: z.string(),
  type: z.string(),
  required: z.boolean(),
  min: z.number().finite().optional(),
  max: z.number().finite().optional(),
  minLength: z.number().int().nonnegative().optional(),
  maxLength: z.number().int().nonnegative().optional(),
  options: z.array(trainerPublicFormFieldOptionSchema).optional(),
}).passthrough()

const trainerPublicFormSchema = z.object({
  schemaId: z.string().min(1),
  version: z.string().min(1),
  status: z.string(),
  customizable: z.boolean(),
  editMode: z.string(),
  revisionSource: z.string(),
  editableByRoles: z.array(z.string()),
  fields: z.array(trainerPublicFormFieldSchema),
  privacy: z.object({
    collectsParticipantEmail: z.boolean(),
    collectsParticipantName: z.boolean(),
  }).passthrough(),
  idempotency: z.object({
    header: z.string(),
    format: z.string(),
  }).passthrough(),
}).passthrough()

const trainerPublicFeedbackEventSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  name: z.string(),
  eventDate: nullableString,
  courseType: nullableString,
  city: nullableString,
  country: nullableString,
  venueName: nullableString,
}).passthrough()

const trainerPublicFeedbackAccessSchema = z.object({
  mode: z.literal('owned_event_token'),
  status: z.enum(['active', 'expired', 'revoked', 'rate_limited']),
  tokenRequired: z.boolean(),
  legacyPublicLinkAccepted: z.boolean(),
  requiresPlatformLogin: z.literal(false),
  collectsParticipantEmail: z.literal(false),
  collectsParticipantName: z.literal(false),
  token: nullableString,
  publicPath: nullableString,
  qrPayload: nullableString,
  expiresAt: nullableString,
  duplicatePolicy: z.literal('idempotency_key'),
  rateLimit: z.object({
    scope: z.literal('event_idempotency_key'),
    enforced: z.literal(true),
  }).passthrough(),
}).passthrough()

export const trainerEventFeedbackContextResponseSchema = z.object({
  contract_version: z.literal(TRAINER_EVENT_FEEDBACK_READ_CONTRACT_VERSION),
  event: trainerPublicFeedbackEventSchema,
  feedbackAccess: trainerPublicFeedbackAccessSchema,
  formSchema: trainerPublicFormSchema,
  sideEffects: trainerEventFeedbackReadSideEffectsSchema,
}).passthrough()

const trainerEventFeedbackExportSchema = z.object({
  filename: z.string().min(1),
  mimeType: z.literal('text/csv'),
  rowCount: z.number().int().nonnegative(),
  csv: z.string(),
  filters: z.object({
    feedbackType: trainerFeedbackTypeSchema.nullable(),
    status: trainerFeedbackStatusSchema.nullable(),
    source: trainerFeedbackSourceSchema.nullable(),
  }).passthrough(),
}).passthrough()

export const trainerEventFeedbackExportResponseSchema = z.object({
  contract_version: z.literal(TRAINER_EVENT_FEEDBACK_READ_CONTRACT_VERSION),
  export: trainerEventFeedbackExportSchema,
  feedbackEvidence: trainerEventFeedbackEvidenceSchema,
  sideEffects: trainerEventFeedbackReadSideEffectsSchema,
}).passthrough()

const trainerGroupWorkStatusSchema = z.enum(['submitted', 'reviewed', 'archived'])
const trainerGroupWorkSourceSchema = z.enum(['owned_public_intake', 'migration', 'admin', 'trainer_app'])

export const trainerEventGroupWorkSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  eventId: z.string().min(1),
  createdByUserId: z.string().min(1),
  submittedByUserId: z.string().min(1),
  teamName: z.string(),
  prompt: z.string(),
  responseText: z.string(),
  participantCount: z.number().finite().nullable(),
  customAnswers,
  status: z.string(),
  source: z.string(),
  active: z.boolean(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

const trainerEventGroupWorkEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([
    z.literal('trainer_events'),
    z.literal('trainer_group_work_responses'),
  ]),
  projection: z.literal('trainer-group-work-responses'),
  rawPayloadReturned: z.literal(false),
  providerTouched: z.literal(false),
}).passthrough()

export const trainerEventGroupWorkListResponseSchema = z.object({
  contract_version: z.literal(TRAINER_EVENT_FEEDBACK_READ_CONTRACT_VERSION),
  responses: z.array(trainerEventGroupWorkSummarySchema),
  groupWorkEvidence: trainerEventGroupWorkEvidenceSchema,
  sideEffects: trainerEventFeedbackReadSideEffectsSchema,
}).passthrough()

const trainerPublicGroupWorkEventSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  name: z.string(),
  eventDate: nullableString,
  courseType: nullableString,
  city: nullableString,
  country: nullableString,
  venueName: nullableString,
  groupWorkFormId: nullableString,
  groupWorkUrl: nullableString,
}).passthrough()

const trainerPublicGroupWorkAccessSchema = z.object({
  mode: z.literal('owned_event_token'),
  status: z.enum(['active', 'expired', 'revoked', 'rate_limited']),
  tokenRequired: z.boolean(),
  requiresPlatformLogin: z.literal(false),
  collectsParticipantEmail: z.literal(false),
  collectsParticipantName: z.literal(false),
  token: nullableString,
  publicPath: nullableString,
  qrPayload: nullableString,
  expiresAt: nullableString,
  duplicatePolicy: z.literal('idempotency_key'),
}).passthrough()

export const trainerEventGroupWorkContextResponseSchema = z.object({
  contract_version: z.literal(TRAINER_EVENT_FEEDBACK_READ_CONTRACT_VERSION),
  event: trainerPublicGroupWorkEventSchema,
  groupWorkAccess: trainerPublicGroupWorkAccessSchema,
  formSchema: trainerPublicFormSchema,
  groupWorkEvidence: trainerEventGroupWorkEvidenceSchema,
  sideEffects: trainerEventFeedbackReadSideEffectsSchema,
}).passthrough()

const trainerEventGroupWorkExportSchema = z.object({
  filename: z.string().min(1),
  mimeType: z.literal('text/csv'),
  rowCount: z.number().int().nonnegative(),
  csv: z.string(),
  filters: z.object({
    status: trainerGroupWorkStatusSchema.nullable(),
    source: trainerGroupWorkSourceSchema.nullable(),
  }).passthrough(),
}).passthrough()

export const trainerEventGroupWorkExportResponseSchema = z.object({
  contract_version: z.literal(TRAINER_EVENT_FEEDBACK_READ_CONTRACT_VERSION),
  export: trainerEventGroupWorkExportSchema,
  groupWorkEvidence: trainerEventGroupWorkEvidenceSchema,
  sideEffects: trainerEventFeedbackReadSideEffectsSchema,
}).passthrough()

export type TrainerEventFeedbackReadQuery = z.infer<typeof trainerEventFeedbackReadQuerySchema>
export type TrainerEventFeedbackSummary = z.infer<typeof trainerEventFeedbackSummarySchema>
export type TrainerEventFeedbackListResponse = z.infer<typeof trainerEventFeedbackListResponseSchema>
export type TrainerEventFeedbackContextResponse = z.infer<typeof trainerEventFeedbackContextResponseSchema>
export type TrainerEventFeedbackExportResponse = z.infer<typeof trainerEventFeedbackExportResponseSchema>
export type TrainerEventGroupWorkSummary = z.infer<typeof trainerEventGroupWorkSummarySchema>
export type TrainerEventGroupWorkListResponse = z.infer<typeof trainerEventGroupWorkListResponseSchema>
export type TrainerEventGroupWorkContextResponse = z.infer<typeof trainerEventGroupWorkContextResponseSchema>
export type TrainerEventGroupWorkExportResponse = z.infer<typeof trainerEventGroupWorkExportResponseSchema>
